import { Box, Chip, IconButton, Typography, Pagination, Button, CircularProgress, Avatar } from "@mui/material";
import React, { useState } from "react";
import LogoIcon from "../../../assets/images/logo-icon.png";
import variables from "../../../assets/styles/variables.module.scss";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NewChatIcon from "../../../assets/icons/NewChatIcon";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { CHAT_HISTORY_DETAIL } from "../../../Services/Endpoint";
import { Params, useParams } from "react-router-dom";
import LineMdAccount from "../../../assets/icons/LineMdAccount";
import {
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";

type ChatHistoryItem = {
  id: string;
  user_id: string;
  agent_id: string;
  agent_name: string;
  conversation_id: string;
  message_count: number;
  first_message_preview: string;
  last_message_preview: string;
  created_at: string;
  organization_id: string | null;
};

type RecentChatsProps = {
  onBack: () => void;
  historyData: {
    items: ChatHistoryItem[];
    pagination?: { total: number; page: number; perPage: number };
  };
    onPageChange: (page: number) => void;
    clearChat?: () => void;
    onChatSelect: (chatHistoryId: string) => void; // Add this prop
};

type ChatDetail = {
  id: string;
  conversation_id: string;
  messages: Array<{
    id: string;
    sender: 'user' | 'agent';
    content: string;
    timestamp: string;
    metadata?: any;
  }>;
  created_at: string;
  updated_at: string;
};

const RecentChatsByUser: React.FC<RecentChatsProps> = ({ onBack, historyData, onPageChange, clearChat, onChatSelect }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const { agentId, apiKey } = useParams<Params>();


  const chatItems = Array.isArray(historyData.items) ? historyData.items : [];
  const pagination = historyData.pagination;

    const hasNextPage =
      pagination
        ? pagination.page * pagination.perPage < pagination.total
        : false;

    const hasPrevPage =
      pagination
        ? pagination.page > 1
        : false;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  const groupChatsByDate = (chats: ChatHistoryItem[]) => {
    const groups: { [key: string]: ChatHistoryItem[] } = {};
    
    chats.forEach(chat => {
      const dateKey = new Date(chat.created_at).toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(chat);
    });
    
    return Object.entries(groups).sort(([a], [b]) => 
      new Date(b).getTime() - new Date(a).getTime()
    );
  };



  const handleChatClick = (chat: ChatHistoryItem) => {
    onChatSelect(chat.id); // Call the parent handler instead of local state
  };

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>

      {/* Header */}
      <Box
        position="sticky"
        sx={{
          borderBottom: "1px solid #f9f7f7",
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          zIndex: 10,           
          top: 0,
          backgroundColor: "#fff",  
        }}
      >

        <IconButton aria-label="back" onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography
          variant="h6"
          component="div"
          sx={{
            flexGrow: 1,
            textTransform: "capitalize",
            color: variables.blackColor,
            textAlign: "center",
          }}
        >
          Recent chats
        </Typography>
        {/* <IconButton aria-label="back" onClick={clearChat}>
          <NewChatIcon />
        </IconButton> */}
      </Box>

      {/* Chat List */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          {chatItems.length === 0 ? (
            <Typography sx={{ textAlign: "center", mt: 3 }}>
              No recent chats available.
            </Typography>
          ) : (
            (() => {
              const groupedChats = groupChatsByDate(chatItems);
              return groupedChats.map(([dateKey, chats]) => (
                <Box key={dateKey}>
                  {/* Date Header */}
                  <Box sx={{ px: 3.5, py: 1.5, backgroundColor: "#f8f9fa",display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: variables.textGray,
                        fontWeight: 500,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        opacity: 0.5,
                        
                      }}
                    >
                      {formatDate(chats[0].created_at)}
                    </Typography>
                  </Box>
                  
                  {/* Chats for this date */}
                  {chats.map((chat) => (
                    <Box
                      key={chat.id}
                      onClick={() => handleChatClick(chat)}
                      sx={{
                        px: 3.5,
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                          cursor: "pointer",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2.5,
                          py: 2.5,
                          borderBottom: "1px solid #f3f2f2",
                        }}
                      >
                        {/* Logo */}
                        <Box
                          sx={{
                            display: "flex",
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            overflow: "hidden",
                            flexShrink: "0",
                          }}
                        >
                          <img
                            src={LogoIcon}
                            alt="Company Logo"
                            style={{
                              width: "100%",
                              height: "auto",
                              objectFit: "cover",
                            }}
                          />
                        </Box>

                        {/* Chat Details */}
                        <Box sx={{ minWidth: "0", flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            noWrap
                            sx={{
                              lineHeight: "27px",
                              textTransform: "capitalize",
                            }}
                          >
                            {chat.first_message_preview}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: variables.textGray }}
                            noWrap
                          >
                            {chat.last_message_preview}
                          </Typography>
                        </Box>

                        {/* Status, Time & Arrow */}
                        <Box sx={{ flexShrink: "0", display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "space-between", gap: 1 }}>
                          <Box sx={{ display: "flex", gap: 1, alignItems: "end"}}>
                            <Chip
                              label={`${chat.message_count} msgs`}
                              size="small"
                              sx={{
                                height: "22px",
                                backgroundColor: "#f0fdf4",
                                color: "#008236",
                              }}
                            />
                            <KeyboardArrowRightIcon />
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{ 
                              color: variables.textGray,
                              fontSize: "11px",
                              textAlign: "left",
                              lineHeight: "1.2",
                              padding: 0.2,
                      
                            }}
                          >
                            {new Date(chat.created_at).toLocaleTimeString('en-US', { 
                              hour: '2-digit', 
                              minute: '2-digit',
                              hour12: true 
                            })}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ));
            })()
          )}
        </Box>

        {/* Pagination */}
        {pagination && (
          <Pagination
            count={Math.ceil(pagination.total / pagination.perPage)}
            page={pagination.page}
            onChange={(e, page) => {
              if (
                (page > pagination.page && !hasNextPage) ||
                (page < pagination.page && !hasPrevPage)
              ) {
                return;
              }
              onPageChange(page);
            }}
            hideNextButton={!hasNextPage}
            hidePrevButton={!hasPrevPage}
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 1,
              borderTop: "1px solid #f3f2f2",
              background: "#fff",
              opacity: chatItems.length === 0 ? 0.5 : 1,
            }}
          />
        )}

      </Box>
    </Box>
  );
};

export default RecentChatsByUser;
